import { GRADE_CATEGORIES, type Grade, RAW_GRADE } from "../core/primitives/grades";

/** The grade field's list (GradeCombobox): which grades a typed text names, narrows to, and what Enter picks. */

/** One line of the grade field's list: a grade under its grader's heading. */
export type GradeOption = { grade: Grade; group: string };

export const GRADE_OPTIONS: GradeOption[] = [
  { grade: RAW_GRADE, group: "Ungraded" },
  ...GRADE_CATEGORIES.flatMap((category) =>
    category.grades.map((grade) => ({ grade, group: category.id.toUpperCase() })),
  ),
];

/** Whether the field's text names this option: "psa9" names PSA 9. */
export const namesGrade = (option: GradeOption, text: string) => squash(option.grade.label) === squash(text);

/** How the grade field's text reads against the list: nothing, a grade it names, a query it narrows, or text it cannot read. */
export type GradeTextReading = "blank" | "named" | "query" | "unread";

/**
 * The list for what the grade field holds, and how its text reads. A value
 * that is itself one of the options is a chosen grade, not a query: filtering
 * on it would leave a list of one and hide its neighbours, which are exactly
 * what someone reopening the list wants to compare against. Text the list
 * cannot read shows every grade, since that is when the list is wanted.
 */
export function gradeOptionsFor(text: string): { options: GradeOption[]; reading: GradeTextReading } {
  const query = squash(text);
  if (!query) return { options: GRADE_OPTIONS, reading: "blank" };
  if (GRADE_OPTIONS.some((o) => namesGrade(o, text))) return { options: GRADE_OPTIONS, reading: "named" };
  const matches = GRADE_OPTIONS.filter((o) => squash(o.grade.label).includes(query));
  return matches.length > 0 ? { options: matches, reading: "query" } : { options: GRADE_OPTIONS, reading: "unread" };
}

/**
 * Whether Enter picks the highlighted line: one the reviewer moved to, or the
 * grade typed text names or narrows to. An untouched field keeps what it
 * holds, and so does typed text the list cannot read: a pick prices from that
 * grade's comp, over a price typed by hand.
 */
export function gradeEnterPicks(o: {
  option: GradeOption;
  text: string;
  typed: boolean;
  moved: boolean;
  reading: GradeTextReading;
}): boolean {
  if (!o.typed && namesGrade(o.option, o.text)) return false;
  return o.moved || (o.typed && (o.reading === "named" || o.reading === "query"));
}

/** Where the list's highlight starts: on the grade the text names, so Enter keeps it, else the first line. */
export const namedGradeIndex = (options: readonly GradeOption[], text: string) =>
  Math.max(
    0,
    options.findIndex((o) => namesGrade(o, text)),
  );

/** "psa1" matches "PSA 1" and "PSA 10": spacing is noise in a hurry. */
const squash = (text: string) => text.toLowerCase().replace(/\s+/g, "");
